/**
 * 日期工具函數
 */

// 確保 Date 原型擴展已加載
import './datePrototype'

type DateInput = Date | string | null | undefined
type TimeUnit = 'ms' | 'second' | 'minute' | 'hour' | 'day'

// 擴展 Date 原型的類型聲明
declare global {
  interface Date {
    Format(format: string): string
    add(unit: string, value: number): Date
  }
}

/**
 * 格式化日期為指定格式
 * @param date - 日期對象或日期字符串
 * @param format - 格式化字符串，例如 'yyyy/MM/dd hh:mm:ss'
 * @returns 格式化後的日期字符串
 */
export function formatDate(date: DateInput, format: string = 'yyyy/MM/dd hh:mm:ss'): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  return d.Format(format)
}

/**
 * 獲取相對時間描述（如：幾分鐘前，幾小時前等）
 * @param date - 日期對象或日期字符串
 * @returns 相對時間描述
 */
export function fromNow(date: DateInput): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  const now = new Date()
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000) // 差異秒數

  if (diff < 60) {
    return '剛剛'
  } else if (diff < 3600) {
    return `${Math.floor(diff / 60)}分鐘前`
  } else if (diff < 86400) {
    return `${Math.floor(diff / 3600)}小時前`
  } else if (diff < 2592000) {
    return `${Math.floor(diff / 86400)}天前`
  } else if (diff < 31536000) {
    return `${Math.floor(diff / 2592000)}個月前`
  } else {
    return `${Math.floor(diff / 31536000)}年前`
  }
}

/**
 * 將本地日期轉換為 UTC 日期字符串
 * @param date - 日期對象
 * @returns UTC 日期字符串
 */
export function toUTCString(date: Date | null | undefined): string {
  if (!date) return ''
  return date.toISOString()
}

/**
 * 從 UTC 字符串獲取本地日期對象
 * @param utcString - UTC 日期字符串
 * @returns 本地日期對象
 */
export function fromUTCString(utcString: string | null | undefined): Date {
  if (!utcString) return new Date()
  return new Date(utcString)
}

/**
 * 將日期調整為指定時區
 * @param date - 日期對象或日期字符串
 * @param offsetHours - 時區小時偏移量
 * @returns 調整後的日期對象
 */
export function adjustTimezone(date: DateInput, offsetHours: number = 8): Date {
  if (!date) return new Date()

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return new Date()

  return d.add('hour', offsetHours)
}

/**
 * 格式化日期並添加時區調整
 * @param date - 日期對象或日期字符串
 * @param offsetHours - 時區小時偏移量
 * @param format - 格式化字符串
 * @returns 格式化後的日期字符串
 */
export function formatDateWithTimezone(
  date: DateInput,
  offsetHours: number = 8,
  format: string = 'yyyy/MM/dd hh:mm:ss'
): string {
  if (!date) return ''

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return ''

  return d.add('hour', offsetHours).Format(format)
}

/**
 * 檢查日期是否超過指定分鐘
 * @param date - 日期對象或日期字符串
 * @param minutes - 分鐘數
 * @returns 是否超過指定分鐘
 */
export function isOlderThanMinutes(date: DateInput, minutes: number = 60): boolean {
  if (!date) return true

  const d = typeof date === 'string' ? new Date(date) : date

  if (isNaN(d.getTime())) return true

  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)

  return diffMinutes > minutes
}

/**
 * 獲取兩個日期之間的差異（可指定返回單位）
 * @param date1 - 第一個日期
 * @param date2 - 第二個日期
 * @param unit - 返回單位 ('ms', 'second', 'minute', 'hour', 'day')
 * @returns 差異值
 */
export function getDateDiff(date1: DateInput, date2: DateInput, unit: TimeUnit = 'ms'): number {
  if (!date1 || !date2) return 0

  const d1 = typeof date1 === 'string' ? new Date(date1) : date1
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0

  const diffMs = Math.abs(d2.getTime() - d1.getTime())

  switch (unit.toLowerCase() as TimeUnit) {
    case 'second':
      return Math.floor(diffMs / 1000)
    case 'minute':
      return Math.floor(diffMs / 60000)
    case 'hour':
      return Math.floor(diffMs / 3600000)
    case 'day':
      return Math.floor(diffMs / 86400000)
    default:
      return diffMs
  }
}
